import type { FileMeta } from '../../../shared/transfer.js';
import type { FileSink } from './file-sink.js';
import { SaveCancelledError } from './file-sink.js';

/** Minimal typing of the File System Access API, which is not in every TypeScript DOM library. */
type WritableFile = {
  write: (chunk: ArrayBuffer) => Promise<void>;
  close: () => Promise<void>;
  abort: (reason: string) => Promise<void>;
};

type SaveFileHandle = {
  name: string;
  createWritable: () => Promise<WritableFile>;
  getFile: () => Promise<File>;
};

type SavePickerOptions = { suggestedName: string };

export type SavePickerWindow = { showSaveFilePicker?: (options: SavePickerOptions) => Promise<SaveFileHandle> };

/** The DOM library does not declare the picker, so the global window is viewed through the minimal type. */
export const pickerWindow = (): SavePickerWindow => window as unknown as SavePickerWindow;

export const hasSaveFilePicker = (win: SavePickerWindow): boolean => typeof win.showSaveFilePicker === 'function';

/** Lets the user choose the destination first, then writes every chunk straight into that file. */
export const createFileSystemSink = async (win: SavePickerWindow, meta: FileMeta): Promise<FileSink> => {
  if (win.showSaveFilePicker === undefined) {
    throw new Error('showSaveFilePicker is not available in this browser');
  }
  let handle: SaveFileHandle;
  try {
    handle = await win.showSaveFilePicker({ suggestedName: meta.name });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new SaveCancelledError('No destination chosen');
    }
    throw error;
  }
  const writable = await handle.createWritable();
  return {
    kind: 'file-system-access',
    write: (chunk) => writable.write(chunk),
    close: async () => {
      await writable.close();
      return { kind: 'written', location: 'chosen-folder', savedName: handle.name, file: await handle.getFile() };
    },
    abort: (reason) => writable.abort(reason),
  };
};
